/* tutor-1 (C): "BASADO EN:" Y EL PANEL DE ELEGIR DOCUMENTOS.

   ARCHIVO EN ASCII A PROPOSITO.

   Chips naranja suave por documento elegido (con su "x"), chips grises con clip por archivo subido aqui (con su "x" y
   "Subido aqui" como `title`), el chip punteado "Agregar documentos"; sin nada elegido, el renglon gris "Sin
   documentos, el tutor contesta con lo que sabe y con la web."; y, si el presupuesto recorto alguno, el renglon gris
   de `ctx.partial`. El panel flotante: titulo, ayuda, la lista de documentos del alumno con `Casilla` (solo listos y
   parciales, sin bloqueados ni del profe; la MISMA lista que el Panel pide, `GET /documents`), "Listo" en verde y el
   boton "Listo".

   tutor-2 (B.1, B.3, D.3, A2.i):
   - abajo de 768 los chips van en UN renglon que se desplaza de lado (sin partir, barra escondida); los renglones
     grises se quedan debajo; desde 768, como antes (se parten);
   - abajo de 768 el panel de elegir es HOJA desde abajo (`alumno/Ventana`), con "Listo" abajo, principal;
   - el chip gris "Tus analiticas" (sin "x") va primero en la conversacion de Analiticas;
   - mientras un archivo sube, su chip gris dice "Leyendo archivo.pdf..." hasta que llega su `fin`.

   tutor-2 remate (R3): el chip del archivo subido y el de la subida en curso se exportan (`ChipDeArchivo`,
   `ChipSubiendo`): la tarjeta y la hoja de la sesion pintan los MISMOS.

   tutor-3 (B.2, P2, C.6): el panel y la hoja ya no llevan su lista local: pintan `elegidos` y cada toque en una
   casilla llama `alCambiarDocumentos` con la lista nueva EN EL MOMENTO. "Listo" solo cierra; tocar fuera, la "x" de
   la hoja (`tutor.mid.close`) y Escape cierran y CONSERVAN lo marcado; un toque en una casilla nunca cierra. */
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, FileText, Paperclip, Plus, X } from 'lucide-react';
import Casilla from '../alumno/Casilla';
import { AreaDe44 } from '../panel/anatomia';
import Ventana, { BOTON_VENTANA_PRINCIPAL } from '../alumno/Ventana';
import { useEsTelefono } from './pantalla';

const CHIP = 'inline-flex items-center gap-1.5 h-7 pl-2.5 pr-1.5 rounded-full border text-[12px] font-medium max-w-full shrink-0';
const CHIP_GRIS = 'bg-[#1B1B1B] border-[#2C2C2C] text-[#C8C8C8]';

/** El chip gris de un archivo subido a la conversacion: el clip, el nombre y la "x" para quitarlo. */
export function ChipDeArchivo({ archivo, alQuitar }) {
  const { t } = useTranslation('study');
  return (
    <span className={`${CHIP} ${CHIP_GRIS}`} title={t('tutor.ctx.uploaded')} data-testid="tutor-chip-archivo">
      <Paperclip className="w-3 h-3 shrink-0" aria-hidden="true" />
      <span className="truncate">{archivo.nombre}</span>
      <button type="button" onClick={() => alQuitar(archivo.id)} aria-label={t('tutor.photos.remove')}
        className="relative w-5 h-5 rounded-full flex items-center justify-center opacity-85 shrink-0">
        <AreaDe44 />
        <X className="w-[11px] h-[11px]" aria-hidden="true" />
      </button>
    </span>
  );
}

/** El chip gris de una subida en curso: "Leyendo archivo.pdf..." hasta que llega su `fin`. */
export function ChipSubiendo({ subida }) {
  const { t } = useTranslation('study');
  return (
    <span className={`${CHIP} pr-2.5 ${CHIP_GRIS}`} role="status" data-testid="tutor-chip-subiendo">
      <Paperclip className="w-3 h-3 shrink-0 motion-safe:animate-pulse" aria-hidden="true" />
      <span className="truncate">{t('tutor.status.reading', { doc: subida.nombre })}</span>
    </span>
  );
}

/** Los documentos que el tutor puede leer: del alumno, listos o parciales, sin bloquear, sin grupo ni banco. */
export function documentosElegibles(documentos) {
  return (documentos || []).filter((d) => d && (d.status === 'ready' || d.status === 'partial')
    && !d.locked_at && !d.group_id && !d.perfil && d.origin !== 'group_bank');
}

export function nombreDelDocumento(d) {
  return (d && (d.title || d.filename)) || '';
}

function ListaParaElegir({ documentos, marcados, alternar }) {
  const { t } = useTranslation('study');
  return documentos.map((d) => {
    const marcado = marcados.includes(d.id);
    return (
      <button key={d.id} type="button" role="checkbox" aria-checked={marcado} onClick={() => alternar(d.id)}
        className="w-full flex items-center gap-2.5 px-2.5 py-[9px] min-h-[44px] md:min-h-0 rounded-[10px] text-[14px] text-left hover:bg-[#242424] min-w-0 text-[#C8C8C8]"
        data-testid="tutor-elegir-documento">
        <Casilla marcada={marcado} />
        <span className="truncate">{nombreDelDocumento(d)}</span>
        <span className="ml-auto text-[11px] text-[#4ADE80] whitespace-nowrap">{t('tutor.ctx.ready')}</span>
      </button>
    );
  });
}

/** La lista nueva al tocar la casilla de `id`. */
export function alternarDocumento(elegidos, id) {
  const lista = elegidos || [];
  return lista.includes(id) ? lista.filter((x) => x !== id) : [...lista, id];
}

function PanelDeElegir({ documentos, elegidos, alAlternar, alCerrar }) {
  const { t } = useTranslation('study');
  const ref = useRef(null);
  useEffect(() => {
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) alCerrar(); };
    const tecla = (e) => { if (e.key === 'Escape') alCerrar(); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', tecla);
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', tecla); };
  }, [alCerrar]);
  return (
    <div ref={ref} role="dialog" aria-label={t('tutor.ctx.pick_title')}
      className="absolute z-[9] top-9 left-0 w-[340px] max-w-[calc(100%-8px)] bg-[#1B1B1B] border border-[#2C2C2C] rounded-[14px] p-2 shadow-[0_16px_40px_rgba(0,0,0,.55)]"
      data-testid="tutor-elegir">
      <div className="font-['Space_Grotesk'] font-semibold text-[15px] px-2.5 pt-1.5 text-[#F4F4F3]">{t('tutor.ctx.pick_title')}</div>
      <div className="text-[12px] leading-[1.4] text-[#6F6F6F] px-2.5 pt-0.5 pb-1.5">{t('tutor.ctx.pick_hint')}</div>
      <div className="max-h-[300px] overflow-y-auto">
        <ListaParaElegir documentos={documentos} marcados={elegidos || []} alternar={alAlternar} />
      </div>
      <div className="flex justify-end px-1.5 pt-2 pb-0.5">
        <button type="button" onClick={alCerrar}
          className="h-9 px-3.5 rounded-xl bg-[#F27A2E] text-[#160A02] font-semibold text-[14px]" data-testid="tutor-elegir-listo">
          {t('tutor.ctx.done')}
        </button>
      </div>
    </div>
  );
}

/** B.3: abajo de 768, la hoja desde abajo de la casa (asa, esquinas de 24, el velo), con "Listo" abajo. */
function HojaDeElegir({ documentos, elegidos, alAlternar, alCerrar }) {
  const { t } = useTranslation('study');
  return (
    <Ventana titulo={t('tutor.ctx.pick_title')} alCerrar={alCerrar} testid="tutor-elegir" etiquetaId="tutor-elegir-titulo"
      etiquetaCerrar={t('tutor.mid.close')}
      botones={(
        <button type="button" className={BOTON_VENTANA_PRINCIPAL} onClick={alCerrar} data-testid="tutor-elegir-listo">
          {t('tutor.ctx.done')}
        </button>
      )}>
      <p className="-mt-1 mb-2 text-[13px] leading-[1.45] text-[#9A9A9A]">{t('tutor.ctx.pick_hint')}</p>
      <div className="-mx-2.5 pb-1">
        <ListaParaElegir documentos={documentos} marcados={elegidos || []} alternar={alAlternar} />
      </div>
    </Ventana>
  );
}

export default function BasadoEn({
  documentos = [], elegidos = [], archivos = [], recortados = [], alCambiarDocumentos, alQuitarArchivo, abierto,
  alAbrir, alCerrar, subiendo = [], conAnaliticas = false, conRespuestas = false,
}) {
  const { t } = useTranslation('study');
  const telefono = useEsTelefono();
  const porId = {};
  documentos.forEach((d) => { porId[d.id] = d; });
  const visibles = elegidos.filter((id) => porId[id]);
  const vacio = !visibles.length && !archivos.length && !subiendo.length && !conAnaliticas;
  const alternar = (id) => alCambiarDocumentos(alternarDocumento(elegidos, id));
  return (
    <div className="relative z-[5] pt-1 pb-3.5" data-testid="tutor-basado-en">
      <div className="flex items-center gap-2 flex-wrap max-md:flex-nowrap max-md:py-2 max-md:-my-2 max-md:overflow-x-auto max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden"
        data-testid="tutor-basado-en-renglon">
        <span className="text-[12px] text-[#9A9A9A] shrink-0 whitespace-nowrap">{t('tutor.ctx.based_on')}</span>
        {conAnaliticas ? (
          <span className={`${CHIP} pr-2.5 ${CHIP_GRIS}`} data-testid="tutor-chip-analiticas">
            <BarChart3 className="w-3 h-3 shrink-0" aria-hidden="true" />
            <span className="truncate">{t('tutor.sources.analytics')}</span>
          </span>
        ) : null}
        {visibles.map((id) => (
          <span key={id} className={`${CHIP} bg-[#241710] border-[#3A2414] text-[#F7A06A]`} data-testid="tutor-chip-documento">
            <FileText className="w-[13px] h-[13px] shrink-0" aria-hidden="true" />
            <span className="truncate">{nombreDelDocumento(porId[id])}</span>
            <button type="button" onClick={() => alCambiarDocumentos(elegidos.filter((x) => x !== id))} aria-label={t('tutor.photos.remove')}
              className="relative w-5 h-5 rounded-full flex items-center justify-center opacity-85 shrink-0">
              <AreaDe44 />
              <X className="w-[11px] h-[11px]" aria-hidden="true" />
            </button>
          </span>
        ))}
        {archivos.map((a) => <ChipDeArchivo key={a.id} archivo={a} alQuitar={alQuitarArchivo} />)}
        {subiendo.map((s) => <ChipSubiendo key={s.id} subida={s} />)}
        <button type="button" onClick={alAbrir} aria-expanded={abierto}
          className="relative h-7 px-2.5 rounded-full border border-dashed border-[#3A3A3A] text-[#9A9A9A] text-[12px] inline-flex items-center gap-1.5 whitespace-nowrap shrink-0"
          data-testid="tutor-agregar-documentos">
          <AreaDe44 />
          <Plus className="w-3 h-3" aria-hidden="true" />
          {t('tutor.ctx.add')}
        </button>
      </div>
      {/* camara-pills remate (firma #720): el renglon gris solo mientras la conversacion no tiene respuestas. */}
      {vacio && !conRespuestas ? <div className="mt-2 text-[12px] text-[#6F6F6F]" data-testid="tutor-sin-documentos">{t('tutor.ctx.no_docs')}</div> : null}
      {recortados && recortados.length ? (
        <div className="mt-2 text-[12px] text-[#6F6F6F]" data-testid="tutor-recortados">{t('tutor.ctx.partial')}</div>
      ) : null}
      {abierto && telefono ? (
        <HojaDeElegir documentos={documentos} elegidos={elegidos} alCerrar={alCerrar} alAlternar={alternar} />
      ) : null}
      {abierto && !telefono ? (
        <PanelDeElegir documentos={documentos} elegidos={elegidos} alCerrar={alCerrar} alAlternar={alternar} />
      ) : null}
    </div>
  );
}
